import { useState } from 'react';
import {
  cn,
  Button,
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@herit-fe/heritage-ui';
import { Check, ChevronDown } from 'lucide-react';
import { UI_TEXT } from '@/shared/languages';
import { DrawerContent } from './drawer-content';
import { SELECT_TRIGGER_CLASS } from './form-control-style';
import { OptionCard } from './option-card';
import { ScrollArea } from './scroll-area';
import type { SelectFieldProps } from './select-field';

/**
 * 단일 선택(compact 표면) — `SelectField` 의 **비-pc(<lg) 분기**. 팝오버 리스트 대신 bottom `Drawer` 로 옵션을
 * 펼친다(터치 타깃·한 손 조작·앱 정합). 트리거는 heritage `SelectTrigger` 와 **동일 룩**(`SELECT_TRIGGER_CLASS`)
 * 이라 브레이크포인트를 넘나들어도 폼 라인이 흔들리지 않는다.
 *
 * 행 표현은 `PlantSelectSheet` 와 같은 캐논 — `OptionCard`(선택=primary 테두리 + 우측 `Check`) + `ScrollArea`.
 * 선택 즉시 닫힘(단일 선택이라 확인 버튼 불필요). 닫기 버튼은 없다 — bottom drawer 의 드래그 핸들·백드롭이 담당
 * (`DrawerCloseButton` 이 compact 에서 null 인 것과 같은 이유).
 *
 * **조회조건 시트 안 중첩**: base-ui `Drawer` 는 부모 drawer 컨텍스트를 감지해 중첩을 자체 지원하므로
 * (`data-nested-drawer-open`), `FilterShell` 안에서도 별도 배선 없이 동작한다.
 */
export function SelectDrawerField({
  id,
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  size = 'default',
  'aria-label': ariaLabel,
}: SelectFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen} direction="bottom">
      <DrawerTrigger
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        data-size={size}
        className={cn(SELECT_TRIGGER_CLASS, className ?? 'w-full')}
      >
        <span
          className={cn(
            'flex-1 truncate text-left',
            selected ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {selected?.label ?? placeholder ?? UI_TEXT.COMMON_SELECT_PLACEHOLDER}
        </span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </DrawerTrigger>

      <DrawerContent aria-describedby={undefined} className="gap-0 overflow-hidden max-h-[85dvh]!">
        <DrawerHeader className="shrink-0 border-b border-border">
          <DrawerTitle className="text-base font-semibold">
            {label ?? ariaLabel ?? UI_TEXT.COMMON_SELECT}
          </DrawerTitle>
        </DrawerHeader>
        <ScrollArea className="min-h-0 flex-1 flex flex-col">
          <ul className="flex flex-col gap-2 p-4">
            {options.length === 0 ? (
              <p className="px-2 py-8 text-center text-sm text-muted-foreground">
                {UI_TEXT.COMMON_NO_ITEMS}
              </p>
            ) : (
              options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value}>
                    {/* 상호작용은 Button 이, 표현은 OptionCard 가 소유(PlantSelectSheet 정합 — ghost 로 룩 중립화). */}
                    <Button
                      variant="ghost"
                      disabled={option.disabled}
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => handleSelect(option.value)}
                      className="block h-auto w-full rounded-xl border-0 p-0 outline-none hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-100"
                    >
                      <OptionCard
                        selected={isSelected}
                        disabled={option.disabled}
                        trailing={
                          isSelected ? (
                            <Check className="size-4 text-primary" aria-hidden="true" />
                          ) : null
                        }
                      >
                        <span className="truncate font-medium text-foreground">{option.label}</span>
                      </OptionCard>
                    </Button>
                  </li>
                );
              })
            )}
          </ul>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
}
